import { useCallback, useEffect, useMemo, useState, type Dispatch, type SetStateAction } from "react";
import { splitUnitItems } from "@/lib/unitPlacement";
import { Link, NavLink, Outlet, useLocation, useNavigate, useOutletContext, useParams } from "react-router-dom";
import { Check, ChevronDown, ChevronLeft, Lock, Megaphone, X } from "lucide-react";
import { api, errorMessage } from "@/lib/api";
import SyllabusPreview from "@/components/student/SyllabusPreview";
import { gradeText } from "@/lib/gradeLabel";
import { ITEM_ICON, ITEM_LABEL, lockedUntilCopy, subjectAccent } from "./itemTypes";
import type { StudentCourse } from "./MyLearning";
import {
  announcementKey,
  dismissAnnouncementKey,
  dismissAnnouncementKeys,
  loadDismissedAnnouncementKeys,
} from "@/lib/dismissedAnnouncements";
import { PathTrailSkeleton, type PathContainer, type PathData, type PathItem } from "./LearningPath";

export type CourseAnnouncement = {
  id: string;
  courseId: string;
  courseTitle: string | null;
  body: string;
  authorName: string | null;
  at: string;
};

export type GradedNotice = {
  activityId: string;
  courseId: string;
  courseTitle: string;
  type: string;
  title: string | null;
  pointsEarned: number | null;
  pointsPossible: number | null;
  gradedAt: string;
};

export type FinishedItem = {
  activityId: string;
  type: string;
  title: string | null;
  pointsEarned: number | null;
  pointsPossible: number | null;
  awaitingMarking: boolean;
  completedAt: string;
};

export type CourseOutlet = {
  course: StudentCourse & { gradeWeights?: Record<string, number> };
  path: PathData;
  announcements: CourseAnnouncement[];
  graded?: GradedNotice[];
  recent?: FinishedItem[];
};

/** Path and Grades live on the student rail. This page keeps Home and Syllabus. */
const TABS = [
  { to: ".", end: true, label: "Home" },
  { to: "syllabus", end: false, label: "Syllabus" },
] as const;

function formatPoints(n: number) {
  if (!Number.isFinite(n)) return "0";
  const rounded = Math.round(n * 100) / 100;
  return Number.isInteger(rounded) ? String(rounded) : String(rounded);
}

function startVerb(type: string) {
  switch (type) {
    case "reading":
      return "Start reading";
    case "video":
      return "Watch the video";
    case "audio":
      return "Listen";
    case "slides":
      return "Open the slides";
    case "key_terms":
      return "Learn the words";
    case "homework":
      return "Start the homework";
    case "assessment":
    case "exam":
      return "Start";
    default:
      return "Start";
  }
}

function postedLabel(iso: string) {
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return "";
  return then.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function dueLabel(iso: string) {
  const due = new Date(iso);
  const days = Math.round((due.getTime() - Date.now()) / 86400000);
  if (days < 0) return { text: "Overdue", late: true };
  if (days === 0) return { text: "Today", late: false };
  if (days === 1) return { text: "Tomorrow", late: false };
  if (days < 7) return { text: due.toLocaleDateString(undefined, { weekday: "long" }), late: false };
  return { text: due.toLocaleDateString(), late: false };
}

function flattenItems(path: PathData): PathItem[] {
  const out: PathItem[] = [...(path.courseItems || [])];
  const walk = (node: PathContainer) => {
    out.push(...(node.items || []));
    (node.children || []).forEach(walk);
  };
  (path.containers || []).forEach(walk);
  return out;
}

export default function CourseHome() {
  const { courseId = "" } = useParams();
  const location = useLocation();
  const [data, setData] = useState<CourseOutlet | null>(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!courseId) return;
    setError("");
    try {
      const env = await api<{ success: boolean; data: CourseOutlet }>(
        `/api/student/courses/${encodeURIComponent(courseId)}`,
      );
      setData(env.data);
    } catch (err) {
      setData(null);
      setError(errorMessage(err));
    }
  }, [courseId]);

  useEffect(() => {
    setData(null);
    void load();
  }, [load]);

  if (error) {
    return (
      <div className="student-page">
        <Link className="student-text-link" to="/student/learning">
          <ChevronLeft size={15} /> My Learning
        </Link>
        <div className="student-inline-error" role="alert" style={{ marginTop: 12 }}>
          {error}
        </div>
      </div>
    );
  }

  if (!data) {
    return <CourseOpeningSkeleton pathname={location.pathname} />;
  }

  const accent = subjectAccent(data.course.title);

  return (
    <div className="student-page" style={{ ["--student-accent" as string]: accent }}>
      <Link className="student-text-link" to="/student/learning">
        <ChevronLeft size={15} /> My Learning
      </Link>
      <div className="student-page-head" style={{ marginTop: 10 }}>
        <span className="student-eyebrow">Class</span>
        <h1>{data.course.title}</h1>
        {data.course.hasPublishedWork ? (
          <p>
            {data.course.activitiesDone} of {data.course.activitiesTotal} activities
            {data.course.grade?.percent != null ? ` · ${gradeText(data.course.grade)}` : ""}
          </p>
        ) : (
          <p>Your teacher has not published work here yet.</p>
        )}
      </div>

      <nav className="student-classtabs" aria-label="This class">
        {TABS.map((tab) => (
          <NavLink
            key={tab.label}
            to={tab.to}
            end={tab.end}
            className={({ isActive }) => `student-classtabs__tab${isActive ? " is-active" : ""}`}
          >
            {tab.label}
          </NavLink>
        ))}
      </nav>

      <Outlet context={data} />
    </div>
  );
}

function placeOf(path: PathData, activityId: string | null) {
  if (!activityId) return { unitId: path.containers[0]?.id || "", lessonId: "" };
  for (const unit of path.containers) {
    if (unit.items.some((item) => item.id === activityId)) return { unitId: unit.id, lessonId: "" };
    for (const lesson of unit.children) {
      if (lesson.items.some((item) => item.id === activityId)) {
        return { unitId: unit.id, lessonId: lesson.id };
      }
    }
  }
  return { unitId: path.containers[0]?.id || "", lessonId: "" };
}

function countLabel(done: number, total: number) {
  if (!total) return "Nothing in here yet";
  return `${done} of ${total}`;
}

/**
 * The class, as units you can open. This is the home of My Learning → class.
 * The path keeps the trail. Grades keeps the scores. This page is the contents.
 */
function ClassOutline({
  path,
  onOpen,
}: {
  path: PathData;
  onOpen: (activityId: string) => void;
}) {
  const upNextId = path.upNext?.activityId || null;
  const [openUnits, setOpenUnits] = useState<Set<string>>(() => {
    const place = placeOf(path, upNextId);
    return new Set(place.unitId ? [place.unitId] : []);
  });
  const [openLessons, setOpenLessons] = useState<Set<string>>(() => {
    const place = placeOf(path, upNextId);
    return new Set(place.lessonId ? [place.lessonId] : []);
  });

  const lessonIds = path.containers.flatMap((unit) => unit.children.map((lesson) => lesson.id));
  const allOpen =
    path.containers.every((unit) => openUnits.has(unit.id)) &&
    lessonIds.every((id) => openLessons.has(id));

  function toggle(set: Dispatch<SetStateAction<Set<string>>>, id: string) {
    set((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  if (!path.containers.length && !path.courseItems.length) return null;

  return (
    <section className="student-outline" aria-label="Class contents">
      <div className="student-section__head">
        <span className="student-eyebrow">In this class</span>
        {path.containers.length > 0 ? (
          <button
            type="button"
            className="student-text-link"
            onClick={() => {
              if (allOpen) {
                setOpenUnits(new Set());
                setOpenLessons(new Set());
                return;
              }
              setOpenUnits(new Set(path.containers.map((unit) => unit.id)));
              setOpenLessons(new Set(lessonIds));
            }}
          >
            {allOpen ? "Close all" : "Open all"}
          </button>
        ) : null}
      </div>

      {path.containers.map((unit) => {
        const open = openUnits.has(unit.id);
        return (
          <div
            key={unit.id}
            className={`student-outline__unit${open ? " is-open" : ""}`}
          >
            <button
              type="button"
              className="student-outline__bar"
              aria-expanded={open}
              onClick={() => toggle(setOpenUnits, unit.id)}
            >
              <ChevronDown
                size={18}
                aria-hidden
                className={`student-outline__chevron${open ? "" : " is-shut"}`}
              />
              <span className="student-outline__main">
                <span className="student-outline__kicker">{unit.label}</span>
                <span className="student-outline__title">{unit.title || unit.label}</span>
                <span className="student-outline__meter" aria-hidden>
                  <i style={{ width: `${unit.percent}%` }} />
                </span>
              </span>
              <span className="student-outline__count">{countLabel(unit.done, unit.total)}</span>
            </button>
            {open ? (
              <div className="student-outline__body">
                {splitUnitItems(unit.items).before.map((item) => (
                  <div key={item.id} className="student-outline__items student-outline__items--unit">
                    <OutlineItem
                      item={item}
                      upNext={upNextId === item.id}
                      onOpen={onOpen}
                    />
                  </div>
                ))}
                {unit.children.map((lesson) => {
                  const lessonOpen = openLessons.has(lesson.id);
                  return (
                    <div key={lesson.id} className="student-outline__lesson">
                      <button
                        type="button"
                        className="student-outline__lessonbar"
                        aria-expanded={lessonOpen}
                        onClick={() => toggle(setOpenLessons, lesson.id)}
                      >
                        <ChevronDown
                          size={16}
                          aria-hidden
                          className={`student-outline__chevron${lessonOpen ? "" : " is-shut"}`}
                        />
                        <span className="student-outline__main">
                          <span className="student-outline__lesson-kicker">
                            {lesson.typeLabel} {lesson.label}
                          </span>
                          <span className="student-outline__lesson-title">
                            {lesson.title || lesson.label}
                          </span>
                        </span>
                        <span className="student-outline__count">
                          {countLabel(lesson.done, lesson.total)}
                        </span>
                      </button>
                      {lessonOpen ? (
                        <div className="student-outline__items">
                          {lesson.items.map((item) => (
                            <OutlineItem
                              key={item.id}
                              item={item}
                              upNext={upNextId === item.id}
                              onOpen={onOpen}
                            />
                          ))}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
                {splitUnitItems(unit.items).after.length ? (
                  <div className="student-outline__items student-outline__items--unit">
                    {splitUnitItems(unit.items).after.map((item) => (
                      <OutlineItem
                        key={item.id}
                        item={item}
                        upNext={upNextId === item.id}
                        onOpen={onOpen}
                      />
                    ))}
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        );
      })}

      {path.courseItems.length > 0 ? (
        <div className="student-card" style={{ marginTop: 0 }}>
          {path.courseItems.map((item) => (
            <OutlineItem key={item.id} item={item} upNext={upNextId === item.id} onOpen={onOpen} />
          ))}
        </div>
      ) : null}
    </section>
  );
}

function OutlineItem({
  item,
  upNext,
  onOpen,
}: {
  item: PathItem;
  upNext: boolean;
  onOpen: (activityId: string) => void;
}) {
  const done = item.status === "completed";
  const locked = Boolean(item.locked && !done);
  const Icon = ITEM_ICON[item.type] || ITEM_ICON.reading;
  const hasScore = item.pointsEarned != null && item.pointsPossible;
  const openLocked = locked && item.unlocksWith?.activityId;
  const bits = [ITEM_LABEL[item.type] || item.type];
  if (locked) bits.push(lockedUntilCopy(item.unlocksWith?.title));
  else if (item.dueAt && !done) bits.push(dueLabel(item.dueAt).text);

  return (
    <button
      type="button"
      className={`student-row student-outline__item${upNext ? " is-next" : ""}${locked ? " student-row--locked" : ""}`}
      disabled={Boolean(locked && !openLocked)}
      onClick={() => {
        if (locked) {
          if (item.unlocksWith?.activityId) onOpen(item.unlocksWith.activityId);
          return;
        }
        onOpen(item.id);
      }}
    >
      <span className={`student-outline__type${done ? " is-done" : ""}${upNext ? " is-next" : ""}`}>
        <Icon size={15} aria-hidden />
      </span>
      <span style={{ textAlign: "left", minWidth: 0, flex: 1 }}>
        <strong>{item.title || "Untitled activity"}</strong>
        <span className="student-row__sub">{bits.filter(Boolean).join(" · ")}</span>
      </span>
      {locked ? (
        <Lock size={14} aria-hidden style={{ color: "var(--s-locked-ink)", flex: "none" }} />
      ) : hasScore ? (
        <span className="student-row__mark">
          {formatPoints(item.pointsEarned as number)}
          <span className="student-row__of"> / {formatPoints(item.pointsPossible as number)}</span>
        </span>
      ) : done ? (
        <span className="student-row__when">
          <Check size={13} aria-hidden /> Done
        </span>
      ) : null}
    </button>
  );
}

export function CourseHomePanel() {
  const { course, path, announcements } = useOutletContext<CourseOutlet>();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(loadDismissedAnnouncementKeys);

  const upNext = useMemo(() => {
    const items = flattenItems(path);
    const fromServer = path.upNext
      ? items.find((item) => item.id === path.upNext?.activityId) || null
      : null;
    if (fromServer && !fromServer.locked) return fromServer;
    return items.find((item) => item.status !== "completed" && !item.locked) || fromServer;
  }, [path]);

  const visibleAnnouncements = announcements.filter((item) => !dismissed.has(announcementKey(item)));
  const canStart = Boolean(upNext && !upNext.locked);

  return (
    <>
      {upNext ? (
        <div className="student-card" style={{ marginTop: 4 }}>
          <span className="student-eyebrow">Continue</span>
          <h2 style={{ margin: "4px 0 6px", fontSize: "1.15rem" }}>{upNext.title || "Untitled activity"}</h2>
          <p className="student-row__sub" style={{ margin: "0 0 12px" }}>
            {ITEM_LABEL[upNext.type] || upNext.type}
            {upNext.locked ? ` · ${lockedUntilCopy(upNext.unlocksWith?.title)}` : ""}
          </p>
          {canStart ? (
            <button
              type="button"
              className="student-btn"
              onClick={() => navigate(`/student/activity/${upNext.id}`)}
            >
              {upNext.status === "in_progress" ? "Keep going" : startVerb(upNext.type)}
            </button>
          ) : (
            <button
              type="button"
              className="student-btn student-btn--ghost"
              disabled={!upNext.unlocksWith?.activityId}
              onClick={() => {
                if (upNext.unlocksWith?.activityId) {
                  navigate(`/student/activity/${upNext.unlocksWith.activityId}`);
                }
              }}
            >
              {upNext.unlocksWith?.title
                ? `Go to ${upNext.unlocksWith.title}`
                : "Finish the previous activity first"}
            </button>
          )}
        </div>
      ) : course.hasPublishedWork ? (
        <div className="student-card" style={{ marginTop: 4 }}>
          <strong>You are all caught up in this class</strong>
          <p className="student-row__sub">New work will show here when your teacher publishes it.</p>
        </div>
      ) : null}

      {visibleAnnouncements.length > 0 && (
        <div className="student-card" style={{ marginTop: 15 }}>
          <div className="student-section__head" style={{ marginBottom: 8 }}>
            <span className="student-eyebrow">
              <Megaphone size={12} aria-hidden /> Teacher updates
            </span>
            {visibleAnnouncements.length > 1 ? (
              <button
                type="button"
                className="student-text-link"
                onClick={() =>
                  setDismissed(dismissAnnouncementKeys(visibleAnnouncements.map(announcementKey)))
                }
              >
                Clear all
              </button>
            ) : null}
          </div>
          {visibleAnnouncements.map((item) => (
            <div key={item.id} className="student-announcement">
              <div>
                <p>{item.body}</p>
                <span className="student-row__sub">
                  {[item.authorName, postedLabel(item.at)].filter(Boolean).join(" · ")}
                </span>
              </div>
              <button
                type="button"
                className="student-announcement__dismiss"
                aria-label="Dismiss announcement"
                onClick={() => setDismissed(dismissAnnouncementKey(announcementKey(item)))}
              >
                <X size={14} aria-hidden />
              </button>
            </div>
          ))}
        </div>
      )}

      <ClassOutline path={path} onOpen={(activityId) => navigate(`/student/activity/${activityId}`)} />
    </>
  );
}

export function CourseGrades() {
  const { course, path, graded = [] } = useOutletContext<CourseOutlet>();
  const navigate = useNavigate();
  const recentlyGraded = useMemo(
    () => new Set(graded.map((row) => row.activityId)),
    [graded],
  );
  const items = useMemo(() => {
    const order = new Map(graded.map((row, i) => [row.activityId, i]));
    return flattenItems(path)
      .filter((item) => item.pointsPossible || item.pointsEarned != null || item.awaitingMarking)
      .slice()
      .sort((a, b) => {
        const aNew = order.has(a.id) ? (order.get(a.id) as number) : 999;
        const bNew = order.has(b.id) ? (order.get(b.id) as number) : 999;
        if (aNew !== bNew) return aNew - bNew;
        const aScore = a.pointsEarned != null ? 0 : 1;
        const bScore = b.pointsEarned != null ? 0 : 1;
        if (aScore !== bScore) return aScore - bScore;
        const aWait = a.awaitingMarking ? 0 : 1;
        const bWait = b.awaitingMarking ? 0 : 1;
        return aWait - bWait;
      });
  }, [path, graded]);
  const scored = items.filter((item) => item.pointsEarned != null && item.pointsPossible);
  const unmarked = items.filter((item) => !(item.pointsEarned != null && item.pointsPossible));
  const grade = course.grade;

  const hasGrade = grade?.percent != null;
  const overallLabel = hasGrade
    ? [
        grade.letter ? `Letter grade ${grade.letter}` : null,
        `${grade.percent} percent`,
        grade.pointsEarned != null && grade.pointsPossible != null
          ? `${formatPoints(grade.pointsEarned)} of ${formatPoints(grade.pointsPossible)} points`
          : null,
      ]
        .filter(Boolean)
        .join(", ")
    : "No marks yet";

  return (
    <div style={{ marginTop: 4 }}>
      <div className="student-card">
        <span className="student-eyebrow">This class</span>
        {hasGrade ? (
          <div className="student-grade-summary" aria-label={overallLabel}>
            {grade.letter ? (
              <div className="student-grade-summary__letter" aria-hidden>
                {grade.letter}
              </div>
            ) : null}
            <div className="student-grade-summary__main">
              <p className="student-grade-summary__percent">{grade.percent}%</p>
              {grade.pointsEarned != null && grade.pointsPossible != null ? (
                <p className="student-grade-summary__points">
                  <strong>{formatPoints(grade.pointsEarned)}</strong>
                  <span> / {formatPoints(grade.pointsPossible)}</span>
                </p>
              ) : null}
            </div>
          </div>
        ) : (
          <h2 style={{ margin: "4px 0 6px", fontSize: "1.15rem" }}>No marks yet</h2>
        )}
        <p className="student-row__sub" style={{ marginTop: 10 }}>
          {hasGrade
            ? "Points are on work already marked."
            : "Grades show here after work is marked."}
          {grade?.awaitingCount ? ` ${grade.awaitingCount} waiting on your teacher.` : ""}
          {course.activitiesTotal
            ? ` ${course.activitiesDone} of ${course.activitiesTotal} activities complete.`
            : ""}
        </p>
        {grade?.weighted ? (
          <>
            <p className="student-row__sub" style={{ marginTop: 8 }}>
              This percent mixes the buckets below. A bucket with no marked work yet is left out until it has a score.
            </p>
            {grade.buckets && grade.buckets.length > 0 ? (
              <ul className="student-syllabus-weights">
                {grade.buckets.map((bucket) => (
                  <li key={bucket.key}>
                    <span>
                      {bucket.label}
                      <span className="student-row__sub">
                        {" "}
                        · {bucket.weight}% of the mix · {bucket.pointsEarned} of {bucket.pointsPossible}
                      </span>
                    </span>
                    <strong>{bucket.percent}%</strong>
                  </li>
                ))}
              </ul>
            ) : null}
          </>
        ) : null}
      </div>
      {scored.length > 0 && (
        <div className="student-card" style={{ marginTop: 15 }}>
          <span className="student-eyebrow">Marked</span>
          {scored.map((item) => {
            const returned = recentlyGraded.has(item.id);
            const waiting = Boolean(item.awaitingMarking);
            return (
              <button
                key={item.id}
                type="button"
                className={`student-row${returned ? " student-row--returned" : ""}`}
                style={{ width: "100%", background: "none", border: 0, cursor: "pointer" }}
                onClick={() => navigate(`/student/activity/${item.id}`)}
              >
                <span style={{ textAlign: "left", minWidth: 0 }}>
                  <strong>{item.title || "Untitled activity"}</strong>
                  <span className="student-row__sub">
                    {ITEM_LABEL[item.type] || item.type}
                    {waiting ? " · Being marked" : " · Graded"}
                  </span>
                </span>
                <span className="student-row__mark">
                  {formatPoints(item.pointsEarned as number)}
                  <span className="student-row__of"> / {formatPoints(item.pointsPossible as number)}</span>
                </span>
              </button>
            );
          })}
        </div>
      )}
      {unmarked.length > 0 && (
        <div className="student-card" style={{ marginTop: 15 }}>
          <span className="student-eyebrow">Not marked yet</span>
          {unmarked.map((item) => {
            const locked = Boolean(item.locked && item.status !== "completed");
            const waiting = Boolean(item.awaitingMarking);
            const status = locked
              ? lockedUntilCopy(item.unlocksWith?.title)
              : waiting
                ? "Being marked"
                : "Not marked";
            const openLocked = locked && item.unlocksWith?.activityId;
            return (
              <button
                key={item.id}
                type="button"
                className={`student-row${locked ? " student-row--locked" : ""}`}
                style={{
                  width: "100%",
                  background: "none",
                  border: 0,
                  cursor: locked && !openLocked ? "default" : "pointer",
                  opacity: locked ? 0.7 : 1,
                }}
                disabled={Boolean(locked && !openLocked)}
                onClick={() => {
                  if (locked) {
                    if (item.unlocksWith?.activityId) {
                      navigate(`/student/activity/${item.unlocksWith.activityId}`);
                    }
                    return;
                  }
                  navigate(`/student/activity/${item.id}`);
                }}
              >
                <span style={{ textAlign: "left", minWidth: 0 }}>
                  <strong>{item.title || "Untitled activity"}</strong>
                  <span className="student-row__sub">
                    {ITEM_LABEL[item.type] || item.type} · {status}
                  </span>
                </span>
                {waiting ? (
                  <span className="student-row__await">Being marked</span>
                ) : item.pointsPossible ? (
                  <span className="student-row__due">{formatPoints(item.pointsPossible)} pts</span>
                ) : null}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function CourseSyllabus() {
  const { course } = useOutletContext<CourseOutlet>();
  const file = course.syllabus?.url ? course.syllabus : null;
  const name = file?.name || "Syllabus";

  if (!file) {
    return (
      <div className="student-card" style={{ marginTop: 4 }}>
        <span className="student-eyebrow">Syllabus</span>
        <p className="student-row__sub" style={{ marginTop: 8 }}>
          Your teacher has not added a syllabus yet.
        </p>
      </div>
    );
  }

  return <SyllabusPreview courseId={course.id} name={name.toLowerCase().endsWith(".pdf") ? name : `${name}.pdf`} />;
}

function classTabFromPath(pathname: string) {
  if (pathname.endsWith("/path") || pathname.endsWith("/path/")) return "path";
  if (pathname.endsWith("/grades") || pathname.endsWith("/grades/")) return "grades";
  if (pathname.endsWith("/syllabus") || pathname.endsWith("/syllabus/")) return "syllabus";
  return "home";
}

function CourseOpeningSkeleton({ pathname }: { pathname: string }) {
  const tab = classTabFromPath(pathname);
  return (
    <div className="student-page" aria-busy="true" aria-live="polite">
      <span className="student-vh">Loading this class</span>
      <div className="student-ghost student-ghost--link" />
      <div className="student-page-head" style={{ marginTop: 10 }}>
        <div className="student-ghost student-ghost--chip" />
        <div className="student-ghost student-ghost--title" />
        <div className="student-ghost student-ghost--line student-ghost--w60" style={{ marginTop: 10 }} />
      </div>
      <nav className="student-classtabs" aria-label="This class">
        {TABS.map((item) => (
          <NavLink
            key={item.label}
            to={item.to}
            end={item.end}
            className={({ isActive }) => `student-classtabs__tab${isActive ? " is-active" : ""}`}
          >
            {item.label}
          </NavLink>
        ))}
      </nav>
      {tab === "path" ? <PathTrailSkeleton /> : <CoursePanelSkeleton />}
    </div>
  );
}

function CoursePanelSkeleton() {
  return (
    <div style={{ marginTop: 4 }}>
      <div className="student-ghost-card">
        <div className="student-ghost student-ghost--chip" />
        <div className="student-ghost student-ghost--line student-ghost--w80" />
        <div className="student-ghost student-ghost--line student-ghost--w60" />
        <div className="student-ghost student-ghost--choice" style={{ width: 140, marginTop: 4 }} />
      </div>
      <div className="student-ghost-card">
        <div className="student-ghost student-ghost--chip" />
        <div className="student-ghost student-ghost--line student-ghost--w70" />
        <div className="student-ghost student-ghost--meta" />
        <div className="student-ghost student-ghost--line student-ghost--w90" />
        <div className="student-ghost student-ghost--meta" />
      </div>
    </div>
  );
}
